import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useOwnedShelters } from "@/hooks/useOwnedShelters";
import { useClaimRequests } from "@/hooks/useClaimRequests";
import { usePendingJoinRequests } from "@/hooks/useTeamJoinRequests";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PanelBadge } from "@/components/panel/PanelBadge";
import {
  Loader2,
  BadgeCheck,
  ExternalLink,
  Search,
  PlusCircle,
  Clock,
  MapPin,
  Check,
  Circle,
  Heart,
  CalendarCheck,
  AlertTriangle,
  Shield,
  Pencil,
  ChevronDown,
  UserPlus,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";

type ChecklistItem = { label: string; done: boolean; href: string; cta: string };

const CompleteProfileCard = ({ total, items }: { total: number; items: ChecklistItem[] }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full p-4 flex items-center gap-3 text-left hover:bg-primary/10 transition-colors"
      >
        <div className="rounded-full bg-primary/10 p-2 shrink-0">
          <BadgeCheck className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground">Profil jest kompletny</h3>
          <p className="text-xs text-muted-foreground">
            Wszystkie {total} sekcji są uzupełnione. {open ? "Kliknij, aby zwinąć." : "Kliknij, aby zobaczyć sekcje."}
          </p>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <ul className="px-3 pb-3 pt-1 space-y-0.5 border-t border-primary/20 bg-background/40">
          {items.map((item) => (
            <li key={item.label}>
              <Link
                to={item.href}
                className="group flex items-center gap-2 text-xs text-foreground/80 rounded-md px-2 py-1.5 hover:bg-primary/10 hover:text-foreground transition-colors"
              >
                <Check className="h-3 w-3 text-primary shrink-0" />
                <span className="flex-1 truncate">{item.label}</span>
                <span className="text-[10px] font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  Edytuj →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const JoinRequestsAlert = ({ shelterId }: { shelterId: string }) => {
  const { data } = usePendingJoinRequests(shelterId);
  const count = data?.length ?? 0;
  if (count === 0) return null;
  const label = count === 1
    ? "Nowa prośba o dołączenie do zespołu"
    : `${count} nowe prośby o dołączenie do zespołu`;
  return (
    <div className="rounded-lg border border-orange-300/60 bg-orange-50 p-3 flex items-start gap-3">
      <UserPlus className="h-4 w-4 text-orange-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 space-y-2">
        <div>
          <h3 className="text-sm font-semibold text-orange-900">{label}</h3>
          <p className="text-xs text-orange-800/90">
            Ktoś poprosił o dołączenie do zespołu Twojego schroniska. Sprawdź szczegóły i zdecyduj, czy chcesz wysłać zaproszenie.
          </p>
        </div>
        <Button asChild size="sm" variant="outline" className="border-orange-400 text-orange-900 hover:bg-orange-100 hover:text-orange-900">
          <Link to="/panel/zespol">
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
            Zobacz prośby
          </Link>
        </Button>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const { data: shelters, isLoading: loadingShelters } = useOwnedShelters(user?.id);
  const { data: requests, isLoading: loadingRequests } = useClaimRequests(user?.id);

  const isLoading = loadingShelters || loadingRequests;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const pendingRequests = (requests ?? []).filter((r) => r.status === "pending");
  const rejectedRequests = (requests ?? []).filter((r) => r.status === "rejected");

  // STATE 3: użytkownik ma min. 1 schronisko -> pełny panel
  if (shelters && shelters.length > 0) {
    return (
      <div className="space-y-4 sm:space-y-8 max-w-4xl">
        <div className="space-y-1">
          <h1 className="text-lg sm:text-2xl font-bold tracking-tight">Pulpit</h1>
          <p className="text-sm text-muted-foreground">
            Zarządzaj profilem swojego schroniska
          </p>
        </div>

        <div className="space-y-4 sm:space-y-6">
          {shelters.map((s) => {
            const profileHref = `/panel/profil/${s.slug}`;
            const needsHref = `/panel/potrzeby/${s.slug}`;
            const checklist = [
              { label: "Nazwa schroniska", done: !!s.name, href: profileHref, cta: "Edytuj profil" },
              { label: "Lokalizacja (miasto i adres)", done: !!(s.city && s.address), href: profileHref, cta: "Edytuj profil" },
              { label: "Opis schroniska", done: !!(s.description && s.description.trim().length > 20), href: profileHref, cta: "Edytuj profil" },
              { label: "Aktualne potrzeby", done: !!(s.current_needs && s.current_needs.trim().length > 0), href: needsHref, cta: "Edytuj potrzeby" },
              { label: "Telefon kontaktowy", done: !!s.phone, href: profileHref, cta: "Edytuj profil" },
              { label: "E-mail kontaktowy", done: !!s.email, href: profileHref, cta: "Edytuj profil" },
              { label: "Informacje o wolontariacie", done: !!(s.volunteer_info && s.volunteer_info.trim().length > 0), href: needsHref, cta: "Edytuj potrzeby" },
            ];
            const completedCount = checklist.filter((c) => c.done).length;
            const percent = Math.round((completedCount / checklist.length) * 100);
            const missing = checklist.filter((c) => !c.done);
            const completed = checklist.filter((c) => c.done);

            const lastUpdatedDate = s.last_updated ? new Date(s.last_updated) : null;
            const daysSinceUpdate = lastUpdatedDate
              ? Math.floor((Date.now() - lastUpdatedDate.getTime()) / (1000 * 60 * 60 * 24))
              : null;
            const isActive = daysSinceUpdate !== null && daysSinceUpdate < 60;
            const isStale = daysSinceUpdate === null || daysSinceUpdate >= 30;

            return (
              <Card key={s.id} className="overflow-hidden border-border/60 shadow-sm">
                {/* Header */}
                <div className="p-4 pb-4 sm:p-6 sm:pb-5 border-b bg-gradient-to-br from-background to-muted/20">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="space-y-2 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-base sm:text-xl font-bold tracking-tight break-words">{s.name}</h2>
                        {s.organization_verified && (
                          <span title="Zweryfikowany profil">
                            <BadgeCheck className="h-5 w-5 text-primary" />
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" />
                        {s.city}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {s.organization_verified && (
                        <PanelBadge
                          variant="verified"
                          icon={BadgeCheck}
                          tooltip="Profil potwierdzony przez przedstawiciela organizacji"
                        >
                          Zweryfikowane
                        </PanelBadge>
                      )}
                      {isActive && (
                        <PanelBadge
                          variant="active"
                          icon={CalendarCheck}
                          tooltip="Dane aktualizowane w ciągu ostatnich 60 dni"
                        >
                          Aktualne
                        </PanelBadge>
                      )}
                      {s.urgent_needs && (
                        <PanelBadge
                          variant="urgent"
                          icon={Heart}
                          tooltip="Schronisko zgłosiło pilne potrzeby"
                        >
                          Pilne potrzeby
                        </PanelBadge>
                      )}
                    </div>
                  </div>

                  {lastUpdatedDate && (
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/70 mt-3">
                      <Clock className="h-3 w-3" />
                      Ostatnia aktualizacja{" "}
                      {formatDistanceToNow(lastUpdatedDate, { locale: pl, addSuffix: true })}
                    </div>
                  )}
                </div>

                <CardContent className="p-3 sm:p-5 space-y-3 sm:space-y-4">
                  {s.my_role === "owner" && <JoinRequestsAlert shelterId={s.id} />}
                  {/* Brak opiekuna profilu - członek może przejąć */}
                  {s.my_role === "member" && !s.has_owner && (() => {
                    const pendingForThis = pendingRequests.find((r) => r.shelter_id === s.id);
                    return (
                      <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 flex items-start gap-3">
                        <Shield className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0 space-y-2">
                          {pendingForThis ? (
                            <>
                              <div>
                                <h3 className="text-sm font-semibold">Twój wniosek o przejęcie czeka na weryfikację</h3>
                                <p className="text-xs text-muted-foreground">
                                  Wysłano: {format(new Date(pendingForThis.created_at), "d MMM yyyy", { locale: pl })}. Zwykle decyzja zapada w 1-3 dni roboczych. Damy znać mailem. Do tego czasu możesz dalej edytować dane i potrzeby jako członek zespołu.
                                </p>
                              </div>
                              <p className="text-[11px] text-muted-foreground">
                                Wniosek długo czeka? Kliknij przycisk „Pomoc" w prawym dolnym rogu.
                              </p>
                            </>
                          ) : (
                            <>
                              <div>
                                <h3 className="text-sm font-semibold">To schronisko nie ma opiekuna profilu</h3>
                                <p className="text-xs text-muted-foreground">
                                  Poprzedni opiekun zrezygnował. Możesz złożyć wniosek o przejęcie tej roli - po akceptacji administratora zyskasz prawo zapraszania zespołu i pełną kontrolę nad profilem. Do tego czasu nadal możesz edytować dane i potrzeby jako członek zespołu.
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button asChild size="sm" variant="outline">
                                  <Link to={`/panel/przejmij?shelter=${s.id}`}>
                                    <Shield className="h-3.5 w-3.5 mr-1.5" />
                                    Zostań opiekunem profilu
                                  </Link>
                                </Button>
                              </div>
                              <p className="text-[11px] text-muted-foreground">
                                Masz problem? Kliknij przycisk „Pomoc" w prawym dolnym rogu, aby się z nami skontaktować.
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Przypomnienie o aktualizacji potrzeb */}
                  {isStale && (
                    <div className="rounded-lg border border-orange-300/60 bg-orange-50 p-3 flex items-start gap-3">
                      <AlertTriangle className="h-4 w-4 text-orange-600 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0 space-y-2">
                        <div>
                          <h3 className="text-sm font-semibold text-orange-900">
                            {daysSinceUpdate === null
                              ? "Brak aktualizacji potrzeb"
                              : `Dane mogą być nieaktualne (${daysSinceUpdate} dni temu)`}
                          </h3>
                          <p className="text-xs text-orange-800/90">
                            Odśwież listę aktualnych potrzeb, aby użytkownicy widzieli, czego naprawdę potrzebujecie.
                          </p>
                        </div>
                        <Button asChild size="sm" variant="outline" className="border-orange-400 text-orange-900 hover:bg-orange-100 hover:text-orange-900">
                          <Link to={`/panel/potrzeby/${s.slug}`}>
                            <Heart className="h-3.5 w-3.5 mr-1.5" />
                            Zaktualizuj potrzeby
                          </Link>
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Stan profilu */}
                  <div className="space-y-2.5">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-sm font-semibold">
                        <span className="tabular-nums">{completedCount}</span> z {checklist.length} sekcji uzupełnionych
                      </h3>
                      <span className="text-xs font-medium tabular-nums text-muted-foreground">
                        {percent}%
                      </span>
                    </div>
                    <Progress
                      value={percent}
                      className="h-1.5 bg-muted [&>div]:bg-primary"
                    />
                  </div>

                  {/* Podsumowanie: co już jest + co warto uzupełnić */}
                  <div className={`grid gap-2.5 ${completed.length > 0 && missing.length > 0 ? "sm:grid-cols-2" : "sm:grid-cols-1"}`}>
                    {completed.length > 0 && missing.length === 0 && (
                      <CompleteProfileCard total={checklist.length} items={completed} />
                    )}
                    {completed.length > 0 && missing.length > 0 && (
                      <div className="rounded-lg border border-border/60 bg-muted/30 p-3 space-y-1.5">
                        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Już uzupełnione ({completed.length})
                        </h3>
                        <TooltipProvider delayDuration={200}>
                          <ul className="space-y-0.5">
                            {completed.map((item) => (
                              <li key={item.label}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Link
                                      to={item.href}
                                      className="group flex items-center gap-2 text-xs text-foreground/80 rounded-md px-1.5 py-1 -mx-1.5 hover:bg-muted hover:text-foreground transition-colors"
                                    >
                                      <Check className="h-3 w-3 text-primary shrink-0" />
                                      <span className="flex-1 truncate">{item.label}</span>
                                      <span className="text-[10px] font-medium text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                        Edytuj →
                                      </span>
                                    </Link>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="text-xs">
                                    <p>Kliknij, aby przejść do: {item.cta}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </li>
                            ))}
                          </ul>
                        </TooltipProvider>
                      </div>
                    )}
                    {missing.length > 0 && (
                      <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-1.5">
                        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-primary">
                          Co warto uzupełnić ({missing.length})
                        </h3>
                        <TooltipProvider delayDuration={200}>
                          <ul className="space-y-0.5">
                            {missing.map((item) => (
                              <li key={item.label}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Link
                                      to={item.href}
                                      className="group flex items-center gap-2 text-xs text-foreground/80 rounded-md px-1.5 py-1 -mx-1.5 hover:bg-primary/10 hover:text-foreground transition-colors"
                                    >
                                      <Circle className="h-3 w-3 text-muted-foreground shrink-0 group-hover:text-primary transition-colors" />
                                      <span className="flex-1 truncate">{item.label}</span>
                                      <span className="text-[10px] font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                        Uzupełnij →
                                      </span>
                                    </Link>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="text-xs">
                                    <p>Kliknij, aby przejść do: {item.cta}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </li>
                            ))}
                          </ul>
                        </TooltipProvider>
                      </div>
                    )}
                  </div>

                  {missing.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Uzupełnij profil, aby użytkownicy łatwiej mogli pomóc schronisku.
                    </p>
                  )}

                  {/* CTA */}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Button asChild size="sm" className="flex-1 sm:flex-none gap-2 px-4 h-9 text-sm">
                      <Link to={`/panel/potrzeby/${s.slug}`}>
                        <Heart className="h-3.5 w-3.5" /> Edytuj potrzeby
                      </Link>
                    </Button>
                    <Button asChild size="sm" variant="outline" className="flex-1 sm:flex-none gap-2 px-4 h-9 text-sm">
                      <Link to={`/panel/profil/${s.slug}`}>
                        <Pencil className="h-3.5 w-3.5" /> Edytuj profil
                      </Link>
                    </Button>
                    <Button asChild size="sm" variant="ghost" className="w-full sm:w-auto gap-1.5 px-3 h-9 text-sm">
                      <Link to={`/schronisko/${s.slug}`}>
                        Zobacz profil
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // STATE 2: jest co najmniej jeden pending wniosek -> ekran oczekiwania
  if (pendingRequests.length > 0) {
    return (
      <div className="max-w-2xl mx-auto space-y-2.5 sm:space-y-5">
        <Card className="overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-orange-400 to-orange-500" />
          <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-orange-100 p-2.5 shrink-0">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div className="space-y-1">
                <CardTitle className="text-sm sm:text-base">Czekamy na weryfikację</CardTitle>
                <CardDescription>
                  Otrzymaliśmy Twój wniosek o dostęp. Zwykle weryfikujemy go w ciągu 1-3 dni roboczych.
                  Damy znać mailem, gdy tylko będzie gotowe.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0 space-y-3">
            {pendingRequests.map((r) => (
              <div
                key={r.id}
                className="rounded-lg border p-3 bg-muted/30 flex items-start gap-3"
              >
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="text-sm flex-1">
                  <div className="font-semibold">{r.shelter_name ?? "Schronisko"}</div>
                  {r.shelter_city && (
                    <div className="text-xs text-muted-foreground">{r.shelter_city}</div>
                  )}
                  <div className="text-xs text-muted-foreground mt-1">
                    Wysłano:{" "}
                    {format(new Date(r.created_at), "d MMM yyyy", { locale: pl })}
                  </div>
                </div>
              </div>
            ))}

            {rejectedRequests.length > 0 && (
              <div className="text-xs text-muted-foreground border-t pt-3">
                Wcześniejsze odrzucone wnioski: {rejectedRequests.length}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground border-t pt-3">
              Wniosek długo czeka lub coś nie działa? Kliknij przycisk „Pomoc" w prawym dolnym rogu, aby się z nami skontaktować.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // STATE 1: brak schronisk i brak pending wniosków -> ekran wyboru
  return (
    <div className="max-w-3xl mx-auto space-y-2.5 sm:space-y-5">
      <div className="text-center space-y-2 py-4">
        <h1 className="text-lg sm:text-2xl font-bold tracking-tight">Witaj w panelu schroniska</h1>
        <p className="text-muted-foreground">
          Wybierz, jak chcesz rozpocząć zarządzanie profilem
        </p>
      </div>

      {rejectedRequests.length > 0 && (
        <Card className="border-orange-300 bg-orange-50/40">
          <CardContent className="p-3 sm:p-4 text-sm space-y-2">
            <div>Twój poprzedni wniosek został odrzucony.</div>
            {rejectedRequests[0].admin_note && (
              <div className="text-muted-foreground">
                <strong>Powód:</strong> {rejectedRequests[0].admin_note}
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              Możesz złożyć nowy wniosek poniżej. Jeśli decyzja wydaje Ci się błędna, kliknij „Pomoc" w prawym dolnym rogu i napisz do nas.
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="group hover:border-primary/50 hover:shadow-md transition-all relative overflow-hidden">
          <div className="absolute top-3 right-3">
            <PanelBadge variant="primary" tooltip="Większość właścicieli wybiera tę opcję">
              Najczęściej
            </PanelBadge>
          </div>
          <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
            <div className="rounded-full bg-primary/10 p-3 w-fit mb-2 group-hover:bg-primary/15 transition-colors">
              <Search className="h-5 w-5 text-primary" />
            </div>
            <CardTitle className="text-sm sm:text-base">Moje schronisko jest na mapie</CardTitle>
            <CardDescription>
              Znajdź swoje schronisko na liście i poproś o dostęp do zarządzania profilem.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0">
            <Button asChild className="w-full">
              <Link to="/panel/przejmij">Znajdź i poproś o dostęp</Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="group hover:border-primary/50 hover:shadow-md transition-all">
          <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
            <div className="rounded-full bg-primary/10 p-3 w-fit mb-2 group-hover:bg-primary/15 transition-colors">
              <PlusCircle className="h-5 w-5 text-primary" />
            </div>
            <CardTitle className="text-sm sm:text-base">Mojego schroniska jeszcze nie ma</CardTitle>
            <CardDescription>
              Dodaj swoje schronisko do mapy. Po akceptacji przez administratora otrzymasz dostęp do panelu.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0">
            <Button asChild variant="outline" className="w-full">
              <Link to="/zglos?owner=1">Dodaj schronisko</Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      <p className="text-xs text-center text-muted-foreground">
        Masz pytania? Napisz na{" "}
        <a href="mailto:kontakt@gdzieschronisko.pl" className="underline hover:text-primary">
          kontakt@gdzieschronisko.pl
        </a>
      </p>
    </div>
  );
};

export default Dashboard;
